/** @jsx jsx */
import { Button, Modal, Spinner } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { css, jsx } from "@emotion/core"
import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { eRoutes, buildRoute } from "../routes"
import { useGlobalState } from "./GlobalStateProvider"
import { useBranchSwitch } from "./MultiBranchSwitcher"
import { buildSearchFilter } from "../../shared/search"
import { useAsyncCallback } from "../hooks/useAsyncCallback"

interface SearchResult {
  id: string
  title: string
  subtitle?: string
  branch?: { id: string; name: string }
  route: string
}

interface GroupedResults {
  key: string
  label: string
  results: SearchResult[]
}

type SearchConfig = {
  key: string
  label: string
  model: string
  attributes: any[]
  filter: (query: string) => any
  branchAssociation?: string
  route: (id: string) => string
  getTitle: (result: any) => string
  getSubtitle?: (result: any) => string | undefined
}

const SEARCH_CONFIGS: SearchConfig[] = [
  {
    key: "quotes",
    label: "Quotes",
    model: "quotes",
    attributes: ["identifier", { name: "branch", attributes: ["name"] }],
    filter: (query: string) => buildSearchFilter(["identifier"], query),
    branchAssociation: "branch",
    route: (id: string) => buildRoute(eRoutes["/quotes/:id/preview"], { id }),
    getTitle: (r: any) => r.identifier || r.id.slice(0, 8) + "...",
    getSubtitle: (r: any) => r.branch?.name,
  },
  {
    key: "orders",
    label: "Orders",
    model: "orders",
    attributes: ["identifier", { name: "branch", attributes: ["name"] }],
    filter: (query: string) => buildSearchFilter(["identifier"], query),
    branchAssociation: "branch",
    route: (id: string) => buildRoute(eRoutes["/orders/:id/preview"], { id }),
    getTitle: (r: any) => r.identifier || r.id.slice(0, 8) + "...",
    getSubtitle: (r: any) => r.branch?.name,
  },
  {
    key: "customers",
    label: "Customers",
    model: "customers",
    attributes: ["name", "account_reference", { name: "branch", attributes: ["name"] }],
    filter: (query: string) => buildSearchFilter(["name", "account_reference"], query),
    branchAssociation: "branch",
    route: (id: string) => buildRoute(eRoutes["/customers/:id"], { id }),
    getTitle: (r: any) => r.name || r.id.slice(0, 8) + "...",
    getSubtitle: (r: any) =>
      [r.account_reference, r.branch?.name].filter(Boolean).join(" · "),
  },
  {
    key: "users",
    label: "Users",
    model: "skeem_users",
    attributes: ["name", "skeem_email"],
    branchAssociation: "branch",
    filter: (query: string) => buildSearchFilter(["name", "skeem_email"], query),
    route: (id: string) => buildRoute(eRoutes["/system/users/:id"], { id }),
    getTitle: (r: any) => r.name || r.skeem_email || r.id.slice(0, 8) + "...",
    getSubtitle: (r: any) => (r.name && r.skeem_email ? r.skeem_email : undefined),
  },
]

export function GlobalSearchPopup({
  isOpen,
  onRequestClose,
}: {
  isOpen: boolean
  onRequestClose: () => void
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [groups, setGroups] = useState<GroupedResults[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const switchBranch = useBranchSwitch()
  const history = useHistory()

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        document.querySelector<HTMLInputElement>("[data-testid='global-search-input']")?.focus()
      }, 50)
    }
  }, [isOpen])

  function closeAndReset() {
    onRequestClose()
    setSearchQuery("")
    setError(null)
    setGroups([])
    setHasSearched(false)
  }

  async function handleNavigateToResult(result: SearchResult) {
    if (result.branch && result.branch.id !== currentUser.branch.id) {
      const canSwitch = currentUser.accessibleBranches.some(b => b.id === result.branch!.id)
      if (canSwitch) {
        await switchBranch(result.branch.id, { path: result.route })
        closeAndReset()
      } else {
        setError("This resource is in a branch you don't have access to")
      }
    } else {
      history.push(result.route)
      closeAndReset()
    }
  }

  const handleSearch = useAsyncCallback(async () => {
    const query = searchQuery.trim()
    if (!query) {
      setError("Please enter a search term")
      return
    }

    setError(null)
    setGroups([])
    setHasSearched(false)

    const accessibleBranchIds = currentUser.accessibleBranches.map(b => b.id)
    const branchArray = accessibleBranchIds.length > 0 ? accessibleBranchIds : [currentUser.branch.id]

    const results = await Promise.all(
      SEARCH_CONFIGS.map(async config => {
        try {
          const searchFilter = config.filter(query)
          const branchFilter = { matches: { [config.branchAssociation as string]: branchArray } }
          const filter = branchFilter
            ? { and: [searchFilter, branchFilter] }
            : searchFilter

          const data = await skeem.fetch({
            [config.model]: {
              attributes: config.attributes,
              filter,
              paginate: { size: 10 },
            },
          } as any)

          const items: any[] = Array.isArray(data) ? data : []
          return {
            key: config.key,
            label: config.label,
            results: items.map(r => ({
              id: r.id,
              title: config.getTitle(r),
              subtitle: config.getSubtitle?.(r),
              branch: r.branch,
              route: config.route(r.id),
            })),
          } as GroupedResults
        } catch (e) {
          console.error(`Error searching ${config.model}:`, e)
          return { key: config.key, label: config.label, results: [] } as GroupedResults
        }
      }),
    )

    setHasSearched(true)
    setGroups(results.filter(g => g.results.length > 0))
  })

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      handleSearch()
    } else if (e.key === "Escape") {
      closeAndReset()
    }
  }

  const totalResults = groups.reduce((sum, g) => sum + g.results.length, 0)

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeAndReset}
      width={680}
      css={css`
        max-width: calc(100% - 24px);
      `}
      render={() => (
        <div
          css={css`
            margin: -16px;
          `}
        >
          {/* Search input */}
          <div
            css={css`
              padding: 20px 20px 16px;
              border-bottom: 1px solid #ebebeb;
            `}
          >
            <Fields.Text
              testId="global-search-input"
              value={searchQuery}
              onChange={(e: string) => {
                setSearchQuery(e)
                setError(null)
                if (!e.trim()) {
                  setGroups([])
                  setHasSearched(false)
                }
                setTimeout(() => {
                  // @ts-ignore
                  document.querySelector("[data-testid='global-search-input']")?.focus()
                }, 50)
              }}
              inputProps={{
                onKeyDown: handleKeyDown,
                placeholder: "Search quotes, orders, customers, users...",
                autoFocus: true,
              }}
              error={error || undefined}
            />
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 10px;
              `}
            >
              <span
                css={css`
                  font-size: 12px;
                  color: #aaa;
                `}
              >
                Quote ID · Order ID · Customer name/account code · User email
              </span>
              <Button
                onClick={handleSearch}
                disabled={handleSearch.calling || !searchQuery.trim()}
              >
                {handleSearch.calling ? "Searching..." : "Search"}
              </Button>
            </div>
          </div>

          {/* Results body */}
          {handleSearch.calling ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 48px 20px;
                color: #888;
                font-size: 14px;
              `}
            >
              <Spinner size={18} color="#888" />
              Searching...
            </div>
          ) : hasSearched && groups.length === 0 ? (
            <div
              css={css`
                padding: 48px 20px;
                text-align: center;
                color: #aaa;
                font-size: 14px;
              `}
            >
              No results found for &ldquo;{searchQuery}&rdquo;
            </div>
          ) : groups.length > 0 ? (
            <div
              css={css`
                max-height: 460px;
                overflow-y: auto;
              `}
            >
              {groups.map((group, groupIndex) => (
                <div key={group.key}>
                  <div
                    css={css`
                      padding: 8px 20px 6px;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      background: #f8f8f8;
                      border-top: ${groupIndex > 0 ? "1px solid #ebebeb" : "none"};
                      border-bottom: 1px solid #ebebeb;
                    `}
                  >
                    <span
                      css={css`
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.06em;
                        color: #777;
                      `}
                    >
                      {group.label}
                    </span>
                    <span
                      css={css`
                        font-size: 11px;
                        font-weight: 500;
                        background: #e3e3e3;
                        color: #666;
                        border-radius: 10px;
                        padding: 1px 7px;
                        line-height: 1.5;
                      `}
                    >
                      {group.results.length}
                    </span>
                  </div>

                  {group.results.map(result => {
                    const isOtherBranch =
                      result.branch && result.branch.id !== currentUser.branch.id
                    const canAccess =
                      !isOtherBranch ||
                      currentUser.accessibleBranches.some(b => b.id === result.branch!.id)

                    return (
                      <div
                        key={result.id}
                        onClick={() => canAccess && handleNavigateToResult(result)}
                        css={css`
                          padding: 11px 20px;
                          display: flex;
                          align-items: center;
                          gap: 12px;
                          border-bottom: 1px solid #f2f2f2;
                          cursor: ${canAccess ? "pointer" : "not-allowed"};
                          opacity: ${canAccess ? 1 : 0.45};
                          transition: background 0.1s ease;

                          &:hover {
                            background: ${canAccess ? "#f0f4ff" : "transparent"};
                          }

                          &:last-child {
                            border-bottom: none;
                          }
                        `}
                      >
                        <div
                          css={css`
                            flex: 1;
                            min-width: 0;
                          `}
                        >
                          <div
                            css={css`
                              font-size: 14px;
                              font-weight: 500;
                              color: #1a1a1a;
                              overflow: hidden;
                              text-overflow: ellipsis;
                              white-space: nowrap;
                            `}
                          >
                            {result.title}
                          </div>
                          {result.subtitle && (
                            <div
                              css={css`
                                font-size: 12px;
                                color: #999;
                                margin-top: 2px;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                              `}
                            >
                              {result.subtitle}
                            </div>
                          )}
                        </div>

                        {isOtherBranch && result.branch && (
                          <span
                            css={css`
                              font-size: 11px;
                              padding: 2px 8px;
                              border-radius: 4px;
                              background: ${canAccess ? "#fff8e1" : "#ffebee"};
                              color: ${canAccess ? "#8a6d00" : "#c62828"};
                              white-space: nowrap;
                              flex-shrink: 0;
                            `}
                          >
                            {result.branch.name}
                          </span>
                        )}

                        {canAccess && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            css={css`
                              flex-shrink: 0;
                              color: #ccc;
                            `}
                          >
                            <path
                              d="M5 2.5L9.5 7L5 11.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div
              css={css`
                padding: 36px 20px;
                text-align: center;
                color: #ccc;
                font-size: 13px;
              `}
            >
              Enter a search term above and press Enter
            </div>
          )}

          {/* Footer */}
          <div
            css={css`
              padding: 10px 20px;
              border-top: 1px solid #ebebeb;
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                font-size: 12px;
                color: #aaa;
              `}
            >
              {hasSearched && totalResults > 0
                ? `${totalResults} result${totalResults !== 1 ? "s" : ""} across ${groups.length} categor${groups.length !== 1 ? "ies" : "y"}`
                : ""}
            </span>
            <Button onClick={closeAndReset} buttonStyle="text">
              Close
            </Button>
          </div>
        </div>
      )}
    />
  )
}
